import {Address, Consumer, Name} from '../_data/reports/consumer/consumer-report';
import {ConsumerRequest, ConsumerRequestConsumer} from '../_data/reports/consumer/consumer-request';
import {isConsumerRequestConsumer, isHrfa} from './type-guards';
import {Hawk} from '../_data/reports/ifs/hawk';
import {equalsIgnoreCase, isNotBlank} from 'src/app/_utils/lang';

export function names(consumer: Consumer | ConsumerRequestConsumer): Name[] {
  return (consumer as Consumer)?.names ?? [];
}

export function primaryName(consumer: Consumer): Name {
  return names(consumer).find(n => n.nameIndicator === '1') ?? null;
}

export function primaryGivenNames(consumer: Consumer | ConsumerRequestConsumer): string {
  if(isConsumerRequestConsumer(consumer)) {
    return formatGivenNames({
      firstName:  consumer.firstName,
      middleName: consumer.middleName,
      lastName:   consumer.lastName,
      suffix:     consumer.suffix
    } as Name);
  } else {
    return formatGivenNames(primaryName(consumer));
  }
}

export function isCreditFreezeMessageExists(consumer: Consumer): boolean {
  return !!consumer?.miscStatements?.find(v => equalsIgnoreCase(v?.remarkType, 'FREEZE'));
}

export function isConsumerStatementExists(consumer: Consumer): boolean {
  return consumer?.consumerStatement?.length > 0;
}

export function creditFreezeMessage(consumer: Consumer): string | undefined {
  return consumer?.miscStatements?.find(v => equalsIgnoreCase(v?.remarkType, 'FREEZE'))?.information;
}

export function isCreditFreezeAndIsNonExemptedMC(consumer: Consumer): boolean {
  let isNonExempted = false;
  if(isCreditFreezeMessageExists(consumer)) {
    if(consumer.subject.onFileDate === '' || consumer.subject.onFileDate == null) {
      isNonExempted = true;
    }
  }
  return isNonExempted;
}

/** If non exempted MC, no information is retrieved from report response except few. Hence populating from the request */
/** If non exempted MC, no information is retrieved from report response except few. Hence populating from the request */
export function getFullNameFromConsumerRequest(consumer: Consumer, request: ConsumerRequest): string {
  const requestConsumer = request?.consumers.find(currentConsumer => currentConsumer.subjectIdentifier === consumer?.subject?.subject - 1);
  if(requestConsumer) {
    return [requestConsumer.firstName, requestConsumer.middleName, requestConsumer.lastName, requestConsumer.suffix].
    filter(s => isNotBlank(s)).join(' ').toUpperCase();
  }
  return '';
}

export function formatGivenNames(name: Name): string {
  return name && `${name.firstName} ${name.middleName ?? ''}`.trim() || null;
}

export function addresses(consumer: Consumer): Address[] {
  return consumer?.addresses ?? [];
}

export function primaryPhone(consumer: Consumer): string {
  return consumer?.phones?.find(p => p.phoneTypeIndicator === 'H')?.telephoneNumber ?? '';
}

export function isHit(consumer: Consumer): boolean {
  return consumer?.subject?.hit === 'Y';
}

export function isAnyHit(consumers: Consumer[]): boolean {
  return !!consumers?.find(c => isHit(c));
}

export function isHrfaNoHit(h: Hawk) {
  return h?.hawkHits === 0;
}

export function hasHrfa(h: any) {
  return isHrfa(h) && (h?.alerts?.length > 0 || h?.hawkMessages?.length > 0);
}

export function isHrfaClear(hawk: Hawk): boolean {
  return !!hawk?.hawkMessages?.find(m => m.messageCode === '0') || isHrfaNoHit(hawk);
}

export function hrfa(hawk: Hawk): Map<string, string> {
  /*
   * Flatten these two types of hawk objects into one standard.
   *
   *"hawk": {
   *  "hawkHits": 1,
   *  "hawkCodes": "0040000000000000000000000000000000000000",
   *  "alerts": [
   *    {
   *      "alertCode": "0040",
   *      "alertMessage": "Current address is a mail drop "
   *    }
   *  ]
   *}
   * //consumer?.hawk?.hawkMessages.find(m => m.messageCode === '0')
   *"hawk": {
   *  "productAddon": {
   *    "productAddonCode": "06500",
   *    "productStatus": "04"
   *  },
   *  "hawkMessages": [
   *    {
   *      "messageCode": "0"
   *    }
   *  ]
   *}
   *"hawk": {
   *  "alerts": null,
   *  "hawkCodes": "0000000000000000000000000000000000000000",
   *  "hawkHits": 0
   *},
   */
  let entries: [any, any][] = [];
  if(hawk?.alerts?.length > 0) {
    //Actual HRFA alerts for IFS Report
    console.info(`${hawk.alerts.length} hrfa alerts`);
    entries = hawk?.alerts?.map(a => [a.alertCode, a.alertMessage]) ?? [];
  } else if(!isHrfaClear(hawk) && hawk?.hawkMessages?.length > 0) {
    //HRFA for Consumer Report
    entries = hawk?.hawkMessages?.map(message => [message.messageCode, message.messageText]) ?? [];
  }
  return Object.fromEntries(entries);

}

/** For each consumer, get the subject identifiers and merge the unique ones all together. */
export function mergeSubjectIdentifiers(consumers: Consumer[] | null) {
  // Run it through a Set and then back to a list to make the items unique
  return [...(new Set(consumers?.flatMap(consumer => consumer.uniqueIdentifier?.subjectIdentifiers ?? [])))];
}
